import { useContext, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';

import { PanelWaiting } from '@/components/hub/PanelNotice';
import { useLimuloPolling, useLimuloPollingGuida } from '@/hooks/useLimuloPolling';
import { cn } from '@/lib/cn';
import { limuloFailure } from '@/lib/limulo/errors';
import { LIMULO_POLL_LISTA_MS } from '@/lib/limulo/polling';
import {
  PASSATI_PER_VOLTA,
  TAB_TORNEI_INIZIALE,
  bollinoMiei,
  divisioneMiei,
  listePubbliche,
  type TabTornei,
} from '@/lib/limulo/quattroTab';
import { SlotListaTornei, type PropsListaTornei } from '@/lib/limulo/slotListaTornei';
import { useGetMyLimuloTournamentsQuery } from '@/services/limuloApi';
import type { LimuloTournament } from '@/types/limulo';

import { LimuloDegraded } from './LimuloDegraded';
import { LimuloTournamentCard } from './LimuloTournamentCard';

type SottoMiei = 'inGara' | 'conclusi';

/**
 * LE QUATTRO TAB — «In arrivo · In corso · Passati · I miei» (I-346), lo stesso componente nelle tre porte: il pannello Eventi
 * della Hub e la lista a tutta pagina (`LimuloTournaments`), la scheda «Tornei» del backgammon (`QgammonTournamentsPanel`).
 * Le regole stanno in `lib/limulo/quattroTab.ts`; qui c'e' solo come si mostrano.
 *
 * Al posto di tre cose che non ci sono piu' (Luca, I-342: «in questo modo nessuna pill mie registrazioni»): la card
 * `MieiTornei` della Hub, la pastiglia `MieiTorneiPastiglia` della pagina (I-317) e i chip d'iscrizione di #562 — la fase li
 * assorbe. La porta verso le mie e' la quarta tab; la pagina `/limulo/mine` a tutta pagina resta, di tutti i giochi.
 *
 * 🔑 **La tab scelta resta finche' la cambia chi guarda.** Si parte da `TAB_TORNEI_INIZIALE` («In arrivo», fissa) e nessun dato
 * la sposta: il polling rilegge le liste ogni 30 s, e una tab che saltasse quando un torneo parte o finisce cambierebbe la
 * schermata sotto il dito.
 *
 * @param pubblicati la lista pubblica GIA' del gioco della porta (`?gameId=`, #577) e gia' passata da `isPublished` e
 *   `nonEUnaSfida`; `null` = non c'e' (attesa, errore): le tre tab pubbliche mostrano `avviso`.
 * @param coda il «Carica altri» della paginazione del SERVER, in fondo alle tab pubbliche.
 * @param vuotoDelGioco la frase quando il gioco non ha NESSUN torneo nelle tre liste pubbliche (#577: il vuoto nomina il gioco,
 *   «Nessun torneo di {{game}} pubblicato.»). Se e' vuota una tab sola, la frase e' quella della tab.
 * @param gameIdMiei il `?gameId=` di `/mine` (derivazione di tma-orcb: dentro la sezione di un gioco anche le mie e il bollino
 *   sono di quel gioco). `undefined` = tutti i giochi; `null` = non si chiede, e «I miei» mostra `avviso`.
 */
function TorneiQuattroTabOggi({
  pubblicati,
  avviso,
  coda,
  vuotoDelGioco,
  gameIdMiei,
}: PropsListaTornei) {
  const { t } = useTranslation('limulo');
  const [tab, setTab] = useState<TabTornei>(TAB_TORNEI_INIZIALE);
  const [sotto, setSotto] = useState<SottoMiei>('inGara');
  const [passatiVisti, setPassatiVisti] = useState(PASSATI_PER_VOLTA);
  const polling = useLimuloPolling();
  // La voce di cache senza argomento resta quella di sempre (la pagina `/limulo/mine`, le code): `gameId` ne apre una sua.
  const mineQuery = useGetMyLimuloTournamentsQuery(
    gameIdMiei === null ? skipToken : gameIdMiei === undefined ? undefined : { gameId: gameIdMiei },
    polling.ritmo,
  );
  useLimuloPollingGuida(polling, mineQuery, LIMULO_POLL_LISTA_MS);
  const now = Date.now();
  // ⚠️ «Non e' mai arrivato» e' `data` assente: un giro fallito accanto a un dato valido non lo cancella (la regola del
  // contatore di prima, `pollingBackoff.ts`). In errore o in attesa: niente bollino, mai uno zero.
  // La difesa per gioco, come quella della lista pubblica (#577): il filtro lo fa il server; se lo ignorasse, una riga d'altro
  // gioco (o senza `gameId`) qui non entra.
  const mie =
    gameIdMiei === null || mineQuery.data === undefined
      ? undefined
      : gameIdMiei === undefined
        ? mineQuery.data
        : mineQuery.data.filter((riga) => riga.gameId === gameIdMiei);
  const bollino = bollinoMiei(mie, now);
  const liste = pubblicati === null ? null : listePubbliche(pubblicati, now);
  const divise = mie === undefined ? null : divisioneMiei(mie, now);

  const voci: { k: TabTornei; label: string }[] = [
    { k: 'arrivo', label: t('list.tabs.upcoming') },
    // «In corso» e' la parola che esiste (`tournamentStatus.in_progress`): Luca, I-346 3, «seconda tab "In corso" (NON "Live")».
    { k: 'corso', label: t('tournamentStatus.in_progress') },
    { k: 'passati', label: t('list.tabs.past') },
    { k: 'miei', label: t('list.tabs.mine') },
  ];

  // ⚠️ Con un'altra pagina in sospeso (`coda`) una tab vuota NON afferma il vuoto: la pagina dopo puo' avere proprio quello.
  const lista = (righe: LimuloTournament[], vuoto: string, inSospeso = false) =>
    righe.length === 0 && inSospeso ? null : righe.length === 0 ? (
      <p data-testid="tornei-tab-vuoto" className="text-sm text-fg-muted">
        {vuoto}
      </p>
    ) : (
      <ul className="flex flex-col gap-2">
        {righe.map((tournament) => (
          <li key={tournament.id}>
            <LimuloTournamentCard tournament={tournament} />
          </li>
        ))}
      </ul>
    );

  let corpo: ReactNode;
  let sottoFiltri: ReactNode = null;
  if (tab === 'miei') {
    const conti = { inGara: divise?.inGara.length ?? null, conclusi: divise?.conclusi.length ?? null };
    // I due sotto-filtri stanno NEL BLOCCO FISSO con le tab (I-400): «mantieni i filtri» vale anche per loro.
    sottoFiltri = (
      /* 32 px a vista, 44 di tocco (`after:` allarga l'area sopra e sotto, condizione di tma-orcb). */
      <div className="flex shrink-0 gap-2" data-testid="tornei-miei-filtri">
        {(['inGara', 'conclusi'] as SottoMiei[]).map((k) => (
          <button
            key={k}
            type="button"
            aria-pressed={sotto === k}
            onClick={() => setSotto(k)}
            className={cn(
              'relative flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 text-xs font-semibold after:absolute after:inset-x-0 after:-inset-y-1.5',
              sotto === k ? 'bg-brand text-brand-fg' : 'card-edge bg-bg-secondary text-fg-muted',
            )}
          >
            {k === 'inGara' ? t('list.mine.racing') : t('list.mine.done')}
            {conti[k] === null ? null : <span className="tabular-nums opacity-70">{conti[k]}</span>}
          </button>
        ))}
      </div>
    );
    corpo = (
      <>
        {gameIdMiei === null ? (
          avviso
        ) : divise === null ? (
          mineQuery.error !== undefined ? (
            <LimuloDegraded failure={limuloFailure(mineQuery.error)} context="list" />
          ) : (
            <PanelWaiting slow={false} />
          )
        ) : sotto === 'inGara' ? (
          lista(divise.inGara, t('list.emptyTab.racing'))
        ) : (
          lista(divise.conclusi, t('list.emptyTab.done'))
        )}
      </>
    );
  } else if (liste === null) {
    corpo = avviso;
  } else if (liste.arrivo.length + liste.corso.length + liste.passati.length === 0 && coda == null) {
    corpo = (
      <p data-testid="tornei-vuoto" className="text-sm text-fg-muted">
        {vuotoDelGioco}
      </p>
    );
  } else if (tab === 'passati') {
    const tutti = liste.passati;
    corpo = (
      <>
        {lista(tutti.slice(0, passatiVisti), t('list.emptyTab.past'), coda != null)}
        {/* Dieci alla volta, dal piu' recente; finiti quelli caricati, il «Carica altri» del server. Mai i due insieme: hanno
            la stessa parola. */}
        {tutti.length > passatiVisti ? (
          <button
            type="button"
            onClick={() => setPassatiVisti((n) => n + PASSATI_PER_VOLTA)}
            className="card-edge rounded-xl bg-bg-secondary px-4 py-3 text-sm font-semibold text-fg-muted"
          >
            {t('list.loadMore')}
          </button>
        ) : (
          coda
        )}
      </>
    );
  } else {
    corpo = (
      <>
        {lista(liste[tab], tab === 'arrivo' ? t('list.emptyTab.upcoming') : t('list.emptyTab.live'), coda != null)}
        {coda}
      </>
    );
  }

  return (
    <>
      {/* 🔴 IL BLOCCO FISSO (Luca, I-400, 05/10: «nella minimizzazione mantieni i filtri In arrivo / In corso / etc.»).
          Le tab — e, nella tab «I miei», i sotto-filtri — restano attaccate in alto mentre la lista scorre e la griglia della
          Hub si chiude. `sticky top-0` vale per il primo antenato che scorre: lo scroller `ownScroll` della Hub, quello della
          scheda del backgammon, o `<main>` nella pagina intera. UN contenitore solo, non due sticky impilati.
          ⚠️ Il fondo e' quasi PIENO (`bg-bg/95` + `backdrop-blur`, lo stesso di `AnimateLibraryModal`): le card scorrono sotto, e su
          un fondo trasparente passerebbero attraverso le scritte.
          Costo dichiarato: 45 px di lista in meno su ogni tab, ~44 in piu' (89 in tutto) nella tab «I miei».
          `shrink-0`: dentro la colonna `h-full overflow-y-auto` del pannello della Hub una riga senza altezza propria si
          schiaccia (la riga dei chip di prima era alta 0 px, misura del 04/10 12:21:51Z). Le tab sono alte 44: il tocco. */}
      <div data-testid="tornei-filtri-fissi" className="sticky top-0 z-10 flex shrink-0 flex-col gap-3 bg-bg/95 backdrop-blur">
        <div role="tablist" data-testid="tornei-quattro-tab" className="flex shrink-0 border-b border-fg-muted/20">
          {voci.map((v) => (
            <button
              key={v.k}
              role="tab"
              type="button"
              aria-selected={tab === v.k}
              onClick={() => setTab(v.k)}
              className={cn(
                'relative flex h-11 min-w-0 flex-1 items-center justify-center px-1 text-xs font-semibold transition-colors',
                tab === v.k ? 'text-brand' : 'text-fg-muted',
              )}
            >
              <span className="relative whitespace-nowrap">
                {v.label}
                {v.k === 'miei' && bollino !== null ? (
                  <>
                    {/* Il bollino in stile notifica (il pallino della TabBar col numero). La cifra e' `aria-hidden`; il lettore
                        di schermo sente la frase intera, dentro la tab (regole di E). */}
                    <span
                      aria-hidden
                      data-testid="tornei-bollino-miei"
                      className="absolute -right-3.5 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold tabular-nums text-brand-fg ring-2 ring-bg"
                    >
                      {bollino}
                    </span>
                    <span className="sr-only">{t('list.tabs.mineBadge', { count: bollino })}</span>
                  </>
                ) : null}
              </span>
              {tab === v.k ? <span aria-hidden className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-brand" /> : null}
            </button>
          ))}
        </div>
        {sottoFiltri}
      </div>
      {corpo}
    </>
  );
}

/**
 * Il punto dove le porte convergono: se sopra di lui c'e' una lista alternativa (`SlotListaTornei`, di norma vuoto) la rende con le STESSE props, altrimenti sono le quattro tab di sopra. Nessun
 * nodo in piu' nel DOM: un componente di funzione che sceglie e non disegna.
 */
export function TorneiQuattroTab(props: PropsListaTornei) {
  const Alternativa = useContext(SlotListaTornei);
  return Alternativa ? <Alternativa {...props} /> : <TorneiQuattroTabOggi {...props} />;
}
